import WKB from 'ol/format/WKB'
import { transformExtent } from 'ol/proj'
import GeoJSON from 'ol/format/GeoJSON'
import { bbox as bboxStrategy } from 'ol/loadingstrategy'
import { Feature } from 'ol'
import Point from 'ol/geom/Point'

// 解析十六进制WKB为几何对象
export function parseHexWKB(hexString, sourceProj, targetProj) {
  if (!hexString) return null

  const format = new WKB()
  try {
    // 读取几何对象，同时进行坐标转换
    const geometry = format.readGeometry(hexString, {
      dataProjection: sourceProj,
      featureProjection: targetProj,
    })
    return geometry
  } catch (error) {
    console.error('解析WKB失败:', error, hexString)
    return null
  }
}

// 定位到点（传入坐标和视图）
export function zoomToPoint(coordinate, view) {
  return new Promise(resolve => {
    view.animate(
      {
        center: coordinate,
        zoom: 18,
        duration: 500,
      },
      resolve
    )
  })
}

// 定位到线
export function zoomToLine(lineGeometry, view) {
  return new Promise(resolve => {
    view.fit(lineGeometry.getExtent(), {
      padding: [100, 100, 100, 100],
      duration: 1000,
      callback: resolve, // 动画完成时调用resolve
    })
  })
}

// 提取线要素的折点（新增）
export function extractVerticesFromLineFeature(feature, vertexSource) {
  const geometry = feature.getGeometry()
  const vertices = []

  if (geometry.getType() === 'LineString') {
    const coordinates = geometry.getCoordinates()
    coordinates.forEach(coord => {
      vertices.push(coord)
    })
  } else if (geometry.getType() === 'MultiLineString') {
    const lines = geometry.getCoordinates()
    lines.forEach(line => {
      line.forEach(coord => {
        vertices.push(coord)
      })
    })
  }

  // 添加折点到折点源
  vertices.forEach(vertex => {
    const vertexFeature = new Feature({
      geometry: new Point(vertex),
      parentFeature: feature, // 记录父要素
      isVertex: true, // 标记为折点
    })
    vertexSource.addFeature(vertexFeature)
  })
}

// 检查视图变化是否显著（优化性能）
export function checkViewportChangeSignificance(map, lastViewState) {
  const view = map.getView()

  // 安全获取当前视图状态
  const currentState = {
    zoom: view.getZoom(),
    center: view.getCenter() || [0, 0],
    resolution: view.getResolution(),
    timestamp: Date.now(),
  }
  // 首次运行直接更新
  if (!lastViewState.center || !lastViewState.resolution) {
    // 将currentState的属性复制到lastViewState
    Object.assign(lastViewState, currentState)
    return true
  }

  // 计算时间差（防止高频刷新）
  const timeDiff = currentState.timestamp - lastViewState.timestamp
  if (timeDiff < 500) return false // 500ms内不重复检测

  // 计算实际移动距离（地图单位）
  const dx = currentState.center[0] - lastViewState.center[0]
  const dy = currentState.center[1] - lastViewState.center[1]
  const movedDistance = Math.sqrt(dx * dx + dy * dy)

  // 计算分辨率变化率（百分比）
  const resolutionRatio = Math.abs((currentState.resolution - lastViewState.resolution) / lastViewState.resolution)

   // 增加更精确的判断条件
  const movedDistanceRatio = movedDistance / (currentState.resolution * map.getSize()[0])
  const shouldUpdate = 
    movedDistanceRatio > 0.3 ||  // 移动超过30%屏幕宽度
    resolutionRatio > 0.3 ||     // 分辨率变化30%
    Math.abs(currentState.zoom - lastViewState.zoom) > 0.5 // 缩放级别变化

  // 更新状态存储
  if (shouldUpdate) {
    Object.assign(lastViewState, currentState)
  }

  return shouldUpdate
}

// 在给定数据源中查找最近的吸附点
export function checkSnapTarget(map, source, pixel, tolerance) {
  if (!source || source.getFeatures().length === 0) return null

  const coordinate = map.getCoordinateFromPixel(pixel)

  let closest = null
  let minDist = Infinity

  const extent = map.getView().calculateExtent()
  const features = source.getFeaturesInExtent(extent)
  const checkFeatures = features.length > 0 ? features : source.getFeatures()

  checkFeatures.forEach(feature => {
    const geometry = feature.getGeometry()
    if (!geometry) return

    if (geometry.getType() === 'Point') {
      const point = geometry.getCoordinates()
      const screenPoint = map.getPixelFromCoordinate(point)
      const dist = Math.sqrt(Math.pow(screenPoint[0] - pixel[0], 2) + Math.pow(screenPoint[1] - pixel[1], 2))

      if (dist < tolerance && dist < minDist) {
        minDist = dist
        closest = { vertex: point, feature: feature }
      }
    } else if (feature.get('isVertex')) {
      const point = geometry.getCoordinates()
      const screenPoint = map.getPixelFromCoordinate(point)
      const dist = Math.sqrt(Math.pow(screenPoint[0] - pixel[0], 2) + Math.pow(screenPoint[1] - pixel[1], 2))

      if (dist < tolerance && dist < minDist) {
        minDist = dist
        closest = { vertex: point, feature: feature }
      }
    } else if (geometry.getType() === 'LineString' || geometry.getType() === 'MultiLineString') {
      const closestPoint = geometry.getClosestPoint(coordinate)
      const screenPoint = map.getPixelFromCoordinate(closestPoint)
      const dist = Math.sqrt(Math.pow(screenPoint[0] - pixel[0], 2) + Math.pow(screenPoint[1] - pixel[1], 2))

      if (dist < tolerance && dist < minDist) {
        minDist = dist
        closest = { vertex: closestPoint, feature: feature }
      }
    }
  })

  return closest
}

// 增量更新 features
function updateFeatures(source, newFeatures) {
  if (!source || typeof source.addFeatures !== 'function') {
    console.error('Invalid source:', source);
    return;
  }

  try {
    // 获取现有特征ID
    const existingIds = source.getFeatures().map(f => f.getId());
    
    // 找出要添加的新特征
    const featuresToAdd = newFeatures.filter(f => {
      const id = f.getId();
      return id && !existingIds.includes(id);
    });
    
    // 找出要移除的旧特征
    const featuresToRemove = source.getFeatures().filter(f => {
      const id = f.getId();
      return id && !newFeatures.some(nf => nf.getId() === id);
    });

    // 批量移除
    if (featuresToRemove.length > 0) {
      // 兼容性处理：优先使用removeFeatures，其次使用removeFeature
      if (typeof source.removeFeatures === 'function') {
        source.removeFeatures(featuresToRemove);
      } else if (typeof source.removeFeature === 'function') {
        featuresToRemove.forEach(f => source.removeFeature(f));
      }
    }
    
    // 批量添加
    if (featuresToAdd.length > 0) {
      source.addFeatures(featuresToAdd);
    }
  } catch (error) {
    console.error('更新特征时出错:', error);
  }
}
// 异步加载要素
export function loadFeaturesAsync(
  CONFIG,
  source,
  layerName,
  extent,
  resolution,
  projection,
  TopologyProjection,
  TopologyCode,
  zoomLevel,
  reviewStatus = 0
) {
  return new Promise((resolve, reject) => {
    // 转换投影到WGS84（GeoServer默认坐标系）
    const wgs84Extent = transformExtent(extent, projection, 'EPSG:4326')

    const url =
      `${CONFIG.SERVICE_PATHS.WFS(CONFIG.WORKSPACE)}?` +
      `service=WFS&` +
      `version=1.1.0&` +
      `request=GetFeature&` +
      `typename=${CONFIG.WORKSPACE}:${layerName}&` +
      `outputFormat=application/json&` +
      `srsname=EPSG:4326&` +
      `bbox=${wgs84Extent.join(',')},EPSG:4326&` +
      `VIEWPARAMS=projectCode:${TopologyProjection};topologyCode:${TopologyCode};zoomLevel:${zoomLevel};reviewStatus:${reviewStatus}`

    fetch(url)
      .then(response => response.json())
      .then(json => {
        const format = new GeoJSON()
        const features = format.readFeatures(json, {
          featureProjection: projection,
          dataProjection: 'EPSG:4326',
        })

        // 增量更新
        updateFeatures(source, features)
        resolve()
      })
      .catch(error => {
        console.error(`加载${layerName}要素失败:`, error)
        reject(error)
      })
  })
}
